import { initializeApp } from 'firebase/app';
import toast from 'react-hot-toast';
import { GoogleAuthProvider, getAuth, signInWithPopup } from 'firebase/auth';
import { useState } from 'react';
import fetchWithAuth from '../FetchWithAuth';
import { API, FirebaseConfig } from '../config/config';
import { Options } from '../models/options';

const UIState = {
  login: 'LOGIN',
  register: 'REGISTER_FORM',
  inactive: 'INACTIVE_ACCOUNT'
};

type AuthState = {
  accessToken?: string;
  displayName?: string;
  loading: boolean;
  uiState?: string;
};

const firebaseApp = initializeApp(FirebaseConfig);
const auth = getAuth(firebaseApp);

const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    accessToken: null,
    displayName: null,
    loading: false,
    uiState: UIState.login
  });

  const invokeGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    const auth = getAuth(firebaseApp);
    provider.addScope('https://www.googleapis.com/auth/contacts.readonly');
    return signInWithPopup(auth, provider);
  };

  // SIGN IN
  const signIn = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true }));
      localStorage.removeItem('accessToken');

      const result = await invokeGoogleSignIn();
      const idToken = await auth.currentUser.getIdToken();

      const user = await fetchWithAuth(`${API.HOST}/api/v1/users/login`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` }
      });
      localStorage.setItem('user', JSON.stringify(user));

      if (!user) {
        throw new Error('User data is null');
      }

      if (!user.active) {
        setAuthState({
          ...authState,
          uiState: UIState.inactive,
          accessToken: idToken,
          loading: false
        });
        return;
      }

      const fullName = `${user.userStaffInfo.firstName} ${user.userStaffInfo.lastName}`;
      const isStaff = user.roles.some((role) => role.roleName === 'STAFF');

      setAuthState({
        ...authState,
        uiState: isStaff ? null : UIState.register,
        accessToken: idToken,
        displayName: fullName,
        loading: false
      });
    } catch (error) {
      console.error('Sign in error:', error);
      setAuthState((prev) => ({ ...prev, loading: false }));
      toast.error('Authentication failed');
    }
  };

  // SETUP INTERCEPTOR REFRESH TOKEN
  const setupTokenRefreshInterceptor = (fetchWithAuthE) => {
    return async (url, options: Options) => {
      try {
        const response = await fetchWithAuthE(url, options);

        if (response.status === 403) {
          const newToken = await auth.currentUser?.getIdToken(true);
          if (newToken) {
            localStorage.setItem('accessToken', newToken);
            options.headers = {
              ...options.headers,
              Authorization: `Bearer ${newToken}`
            };
            return fetchWithAuthE(url, options);
          }
        }
        return response;
      } catch (error) {
        if (error.status === 403) {
          signIn();
        }
        return Promise.reject(error);
      }
    };
  };

  const enhancedFetch = setupTokenRefreshInterceptor(fetchWithAuth);
  return {
    authState,
    setAuthState,
    signIn,
    setupTokenRefreshInterceptor,
    enhancedFetch
  };
};

export { UIState, useAuth };
